<template>
  <div>
    <div>
      <FilterCard :applied-filter-count="appliedFilterCount">
        <b-row>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Created Date"
              label-for="created-date"
            >
              <div class="position-relative">
                <AdvancedDateFilter
                  :date-value="filters['created_at']"
                  :opens="'right'"
                  @update="updateDateRange('created_at', $event)"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="Merchant"
              label-for="merchant"
            >
              <v-select
                v-model="filters['merchant_id']"
                :options="merchantOptions"
                :reduce="option => option.id"
                label="name"
                placeholder="Select Merchant"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group
              label="Ticket Category"
              label-for="ticket-category"
            >
              <v-select
                v-model="filters['ticket_type_id']"
                :options="ticketTypes"
                :reduce="option => option.id"
                label="name"
                placeholder="Select Ticket Category"
              >
                <template v-slot:option="option">
                  {{ option.name }}
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Apply and Clear Filters -->
        <b-row class="mt-1">
          <b-col>
            <filter-buttons
              :filter-data="filters"
              :apply-filters="applyFilters"
              :clear-filters="clearFilters"
            />
          </b-col>
        </b-row>
      </FilterCard>
    </div>
    <div>
      <b-card no-body>
        <div class="m-2">
          <b-row>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
            >
              <div>
                <ReportTableButtons
                  :items="items"
                  :json_fieldz="json_fields"
                  :name="`${downloadReportName} - Page ${page}.xls`"
                  :bulkname="`${downloadReportName}.xls`"
                  :fetch="fetchMerchantWiseTicketReportNoPagination"
                  @refresh="fetchMerchantWiseTicketReport"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <b-overlay
          id="overlay-background"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="items"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="items"
                :data-loading="dataLoading"
              />
            </template>
            <template #head()="{ field }">
              <div class="sortable-header">
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key
                    ? 'ArrowUpIcon'
                    : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
          </b-table>
        </b-overlay>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label class="mr-1">Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block width-125"
              />
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span class="text-muted pagination-text mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
              <b-pagination
                v-model="page"
                :total-rows="(meta.total) ? meta.total : 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BRow,
  BTable,
  VBModal,
  BOverlay,
  BPagination,
} from 'bootstrap-vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import FilterButtons from '@/components/Filter/ReportFilterButtons.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'

const ReportRepository = RepositoryFactory.get('report')
const MerchantRepository = RepositoryFactory.get('merchant')
const ticketRepository = RepositoryFactory.get('ticket')

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    vSelect,
    AdvancedDateFilter,
    BCard,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BOverlay,
    BPagination,
    FilterButtons,
    FilterCard,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      filters: {
        created_at: {
          startDate: null,
          endDate: null,
        },
      },
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 200],
      totalRows: 1,
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      fields: [
        {
          key: 'merchant_name',
          label: 'Merchant',
          thClass: 'text-left',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          key: 'ticket_count',
          label: 'Ticket Count',
          thClass: 'text-right',
          tdClass: 'text-right',
          sortable: true,
        },
      ],
      json_fields: {
        Merchant: {
          field: 'merchant_name',
          callback: value => `"${value}"`,
        },
        'Ticket Count': 'ticket_count',
      },
      downloadReportName: 'Merchant-Wise-Ticket-Report',
      items: [],
      merchantOptions: [],
      ticketTypes: [],
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchMerchantWiseTicketReport()
      },
    },
    deep: true,
    page() {
      this.fetchMerchantWiseTicketReport()
    },
    perPage() {
      this.fetchMerchantWiseTicketReport()
    },
  },
  mounted() {
    this.totalRows = this.items.length
    this.fetchMerchantWiseTicketReport()
    this.getMerchantList()
    this.getTicketTypes()
    this.updateFilterCount()
  },
  methods: {
    async getMerchantList() {
      this.merchantOptions = localStorage.getItem('merchant_list') ? JSON.parse(localStorage.getItem('merchant_list')) : (await MerchantRepository.getMerchantListForDropdown()).data.data
      if (!localStorage.getItem('merchant_list')) localStorage.setItem('merchant_list', JSON.stringify(this.merchantOptions))
    },
    async getTicketTypes() {
      try {
        this.ticketTypes = localStorage.getItem('ticket_types') ? JSON.parse(localStorage.getItem('ticket_types')) : (await ticketRepository.getTicketTypes()).data.data
        if (!localStorage.getItem('ticket_types')) localStorage.setItem('ticket_types', JSON.stringify(this.ticketTypes))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async fetchMerchantWiseTicketReport() {
      this.dataLoading = true
      this.items = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = await ReportRepository.getMerchantWiseTicketReport(this.page, filterQuery, this.perPage)
        this.items = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchMerchantWiseTicketReportNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = await ReportRepository.getMerchantWiseTicketReportNoPagination(filterQuery)
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    applyFilters() {
      this.fetchMerchantWiseTicketReport()
      this.updateFilterCount()
    },
    clearFilters() {
      this.filters = {
        created_at: {
          startDate: null,
          endDate: null,
        },
      }
      this.$nextTick(() => {
        this.fetchMerchantWiseTicketReport()
      })
      this.updateFilterCount()
    },
  },
}
</script>

<style lang="scss">
@import '../../../@core/scss/vue/libs/vue-select';
</style>
